<script lang="ts" setup>
    import '../../../css/choice.css';
    import {popAlert,ModalStyles} from '../../lib/Notification';   
    import {onMounted,reactive,computed} from 'vue';
    import {ISelectedPlan,ISelectedPlans,IRecurringPlan,IRecurringPayment, IRecurringPaymentList} from '../../lib/Models/PaymentPlanProcessModel';  
    import {ErrorReturnMessage as PaySimpleErrorReturnMessage, RecurringPayments} from '../../lib/Api/PaySimpleCreditCard';
    import {hideWindowObject} from '../../lib/Models/CommonItemsModel';  
    import {ErrorReturnMessage,getPaymentPlanProcess} from '../../lib/Api/PaymentPlan';
    import WindowHeaderFooter from '../../components/WindowHeaderFooter.vue';
    import Checkbox from '../../components/Inputs/Checkbox.vue';
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'; 
    import {prettyCurrency} from '../../lib/Formatting/Number';
    import {ActionButtonStyle} from '../../lib/Types/Types'; 
    import {ButtonDisplaySize,ButtonDisplayStyle} from '../../Enums/UiEnums';  
    
    const baseHeaderStyle = "label-process text-center pp-grid-border-top pp-grid-border-bottom pp-grid-border-left text-isalus-primary";
    const baseValueStyle = "value-process pp-grid-border-bottom pp-grid-border-left";
    const formData = reactive <
    {    
        PlanList?: IRecurringPlan[]
    }>({  
    });

    const props = withDefaults(defineProps<{  
        saveEnabled: number 
    }>(),
    {
        saveEnabled: 0
    });
    onMounted(async () => { 
        await getData();
    });
    
    const disableProcessButton = computed<boolean>(() =>{
        if (props.saveEnabled === 0) {
            return true;
        }
        if (!formData.PlanList) {
            return true;
        }
        if (formData.PlanList.filter(item => item.selected && (!item.processMessage || item.processMessage == "")).length == 0) {
            return true;
        }
        return false;
    });
    async function getData() : Promise<void>  {
        let selected_plan_ids : ISelectedPlan[] = [];
        try {
            const selected_items = window.opener.GetSelectionList();
            if (selected_items[0] > 0) {
                selected_plan_ids = selected_items[1].map((item: string | number) => ({paymentPlanId: +item}));
            }
        } catch (e) {}
        const messageTitle = "Payment Plan List"; 
        const payment_plan_input : ISelectedPlans = {RecurringPayments: false, PaymentPlanList: selected_plan_ids};
        const planResults = await getPaymentPlanProcess(payment_plan_input);
        if (!planResults.success) {  
            await popAlert(planResults.error?.error ?? ErrorReturnMessage.planProcess,messageTitle,ModalStyles.danger);
            return;
        }
        formData.PlanList = planResults.success.recurringPlans;
        hideWindowObject("LoadingMessage"); 
    }
    async function closeWindow() : Promise<void> {
        window.close();
    }  
    async function processPayments() : Promise<void> {
        if (!formData.PlanList) {
            return;
        }
        const messageTitle = "Payments"; 
        const recurring_payments : IRecurringPayment[] = formData.PlanList.filter(item => item.selected && ((!item.processMessage || item.processMessage == ""))).map(item => ({PaymentPlanID: item.paymentPlanId, InstallmentAmount: item.installmentAmount}));
        const recurring_payment_list :IRecurringPaymentList = {RecurringPayments: false, PaymentPlanList: recurring_payments};

        const paymentResults = await RecurringPayments(recurring_payment_list);
        if (!paymentResults.success) {  
            await popAlert(paymentResults.error?.error ?? PaySimpleErrorReturnMessage.recurringPayments,messageTitle,ModalStyles.danger);
            return;
        }
        paymentResults.success.forEach(element =>
        {
            if (formData.PlanList) {
                const index = formData.PlanList.findIndex(item => item.paymentPlanId == element.paymentPlanID) ?? -1;
                if (index > -1) {
                    formData.PlanList[index].processMessage = element.processMessage;
                }
            }
        }); 
    }
</script> 
  
<style scoped>
    @import url('../../../css/paymentPlanGrid.css');
</style>
<template> 
    <WindowHeaderFooter
    :headerHeight="3.5"
    :footerHeight="3.5" >
        <template v-slot:WindowHeader>
            <div class="isalus-page-title-large pt-3">Process Non-recurring Payments</div>
        </template>
        <template v-slot:WindowBody>  
            <div class="grid-container-process label-isalus-small">
                <div :class="baseHeaderStyle">Selected</div>
                <div :class="baseHeaderStyle">Plan ID</div>
                <div :class="baseHeaderStyle">Patient</div>
                <div :class="baseHeaderStyle">Due Date</div>
                <div :class="baseHeaderStyle">Amount</div> 
                <div :class="(baseHeaderStyle + ' pp-grid-border-right')">Status</div>
            </div>   
            <div class="grid-container-process label-isalus-small" v-if="(formData.PlanList?.length ?? 0) > 0" v-for="(planRecord,index) in formData.PlanList" :key="`ID${planRecord.paymentPlanId}`">
                <div :class="(baseValueStyle + ' text-center')">
                    <Checkbox v-model="planRecord.selected" :disabled="((planRecord.processMessage ?? '') === '' ? false : true)"/>  
                </div>
                <div :class="(baseValueStyle + ' text-end')">{{planRecord.paymentPlanId}}</div>
                <div :class="baseValueStyle">{{planRecord.patientId}} - {{planRecord.patientName}}</div>
                <div :class="(baseValueStyle + ' text-center' + (planRecord.postDate ? ' text-danger' : ''))">{{planRecord.installmentDueDate}}</div>
                <div :class="(baseValueStyle + ' text-end')">{{prettyCurrency(planRecord.installmentAmount)}}</div> 
                <div :class="(baseValueStyle + ' pp-grid-border-right')">{{planRecord.processMessage}}</div>  
            </div>   
        </template>
        <template v-slot:WindowFooter> 
            <div class="text-end">
                <ActionButtonV2 
                    :action="ActionButtonStyle.Refresh"
                    :text="'Refresh'"
                    :size="ButtonDisplaySize.Small" 
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                    :spinText="'Refreshing'"
                    :performAction="getData"
                    :endingPadding="1"
                />
                <ActionButtonV2 
                    :action="ActionButtonStyle.Save"
                    :disabled="disableProcessButton"
                    :text="'Process'"
                    :size="ButtonDisplaySize.Small" 
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                    :spinText="'Processing'"
                    :performAction="processPayments"
                    :endingPadding="1"
                />
                <ActionButtonV2 
                    :action="ActionButtonStyle.Close"
                    :size="ButtonDisplaySize.Small" 
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                    :spinText="'Closing'"
                    :performAction="closeWindow"
                />
            </div>
        </template>
    </WindowHeaderFooter> 
</template>
